import React, { useState, useEffect } from 'react';
import { timetablesService } from '../../services/timetablesService';

export const SettingsPanel: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [formData, setFormData] = useState({
    discordWebhookEnabled: false,
    discordWebhookUrl: '',
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await timetablesService.getSettings();
      setFormData({
        discordWebhookEnabled: data.discordWebhookEnabled,
        discordWebhookUrl: data.discordWebhookUrl || '',
      });
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const updateData = {
        discordWebhookEnabled: formData.discordWebhookEnabled,
        discordWebhookUrl: formData.discordWebhookEnabled ? formData.discordWebhookUrl : undefined,
      };

      await timetablesService.updateSettings(updateData);

      // Show success message
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestMessage = async () => {
    if (!formData.discordWebhookEnabled || !formData.discordWebhookUrl) {
      alert('Please enable Discord webhooks and provide a webhook URL first.');
      return;
    }

    setSendingTest(true);

    try {
      await timetablesService.sendTestWebhook();
      alert('Test message sent successfully! Check your Discord channel.');
    } catch (error) {
      console.error('Failed to send test message:', error);
      alert('Failed to send test message. Please check your webhook URL and try again.');
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <div className='dashboard-card'>
        <div className='dashboard-card__header'>
          <div className='dashboard-card__title'>Loading Settings...</div>
        </div>
        <div className='dashboard-card__content'>
          <div className='pulse' style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div
              style={{
                height: '20px',
                backgroundColor: 'var(--tertiary-bg)',
                borderRadius: '4px',
                width: '60%',
              }}
            ></div>
            <div
              style={{
                height: '40px',
                backgroundColor: 'var(--tertiary-bg)',
                borderRadius: '4px',
              }}
            ></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='dashboard-card'>
      <div className='dashboard-card__header'>
        <h2 className='dashboard-card__title'>Discord Notifications</h2>
      </div>

      <form onSubmit={handleSave}>
        <div className='dashboard-card__content' style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <input
              type='checkbox'
              id='discord-webhook'
              checked={formData.discordWebhookEnabled}
              onChange={e =>
                setFormData({
                  ...formData,
                  discordWebhookEnabled: e.target.checked,
                })
              }
              style={{
                width: '16px',
                height: '16px',
                marginTop: '2px',
                accentColor: 'var(--primary-red)',
              }}
            />
            <label
              htmlFor='discord-webhook'
              style={{
                fontSize: '0.875rem',
                fontWeight: '500',
                color: 'var(--white-text)',
                cursor: 'pointer',
                lineHeight: '1.4',
              }}
            >
              Send Discord webhook notifications when team member status changes
            </label>
          </div>

          {formData.discordWebhookEnabled && (
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: 'var(--white-text)',
                  marginBottom: '8px',
                }}
              >
                Discord Webhook URL
              </label>
              <input
                type='url'
                value={formData.discordWebhookUrl}
                onChange={e =>
                  setFormData({
                    ...formData,
                    discordWebhookUrl: e.target.value,
                  })
                }
                required={formData.discordWebhookEnabled}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid var(--divider-gray)`,
                  borderRadius: '4px',
                  backgroundColor: 'var(--tertiary-bg)',
                  color: 'var(--white-text)',
                  fontSize: '0.875rem',
                  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                }}
                placeholder='https://discord.com/api/webhooks/...'
                onFocus={e => {
                  const input = e.currentTarget;
                  input.style.borderColor = 'var(--primary-red)';
                  input.style.boxShadow = '0 0 0 2px rgba(227, 27, 35, 0.2)';
                }}
                onBlur={e => {
                  const input = e.currentTarget;
                  input.style.borderColor = 'var(--divider-gray)';
                  input.style.boxShadow = 'none';
                }}
              />
              <div
                style={{
                  marginTop: '12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '16px',
                }}
              >
                <div
                  style={{
                    flex: 1,
                    fontSize: '0.75rem',
                    color: 'var(--light-gray)',
                    padding: '12px',
                    backgroundColor: 'var(--tertiary-bg)',
                    borderRadius: '4px',
                    borderLeft: '3px solid var(--blue-accent)',
                  }}
                >
                  <p style={{ margin: '0 0 8px 0', fontWeight: '500' }}>To get a Discord webhook URL:</p>
                  <ol
                    style={{
                      margin: 0,
                      paddingLeft: '16px',
                      lineHeight: '1.5',
                    }}
                  >
                    <li>Go to your Discord server settings</li>
                    <li>Navigate to Integrations → Webhooks</li>
                    <li>Click "New Webhook" and configure it</li>
                    <li>Copy the webhook URL and paste it here</li>
                  </ol>
                </div>

                <button
                  type='button'
                  onClick={handleSendTestMessage}
                  disabled={sendingTest || !formData.discordWebhookUrl}
                  style={{
                    padding: '8px 16px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: 'var(--white-text)',
                    backgroundColor: sendingTest || !formData.discordWebhookUrl ? 'var(--medium-gray)' : 'var(--blue-accent)',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: sendingTest || !formData.discordWebhookUrl ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.15s ease',
                    opacity: sendingTest || !formData.discordWebhookUrl ? 0.6 : 1,
                    whiteSpace: 'nowrap',
                    minWidth: '140px',
                  }}
                  onMouseOver={e => {
                    if (!sendingTest && formData.discordWebhookUrl) {
                      e.currentTarget.style.backgroundColor = '#1F5F8B'; // Darker blue
                    }
                  }}
                  onMouseOut={e => {
                    if (!sendingTest && formData.discordWebhookUrl) {
                      e.currentTarget.style.backgroundColor = 'var(--blue-accent)';
                    }
                  }}
                >
                  {sendingTest ? 'Sending...' : 'Send test message'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className='dashboard-card__footer'>
          <div></div>
          <button
            type='submit'
            disabled={saving}
            style={{
              padding: '8px 16px',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: 'var(--white-text)',
              backgroundColor: saving ? 'var(--medium-gray)' : 'var(--primary-red)',
              border: 'none',
              borderRadius: '4px',
              cursor: saving ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.15s ease',
              opacity: saving ? 0.6 : 1,
            }}
            onMouseOver={e => {
              if (!saving) {
                e.currentTarget.style.backgroundColor = 'var(--dark-red)';
              }
            }}
            onMouseOut={e => {
              if (!saving) {
                e.currentTarget.style.backgroundColor = 'var(--primary-red)';
              }
            }}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>

      {formData.discordWebhookEnabled && (
        <div
          style={{
            margin: '16px',
            marginTop: '0',
            padding: '16px',
            backgroundColor: 'rgba(52, 152, 219, 0.1)',
            border: '1px solid var(--blue-accent)',
            borderRadius: '4px',
          }}
        >
          <h3
            style={{
              fontSize: '0.875rem',
              fontWeight: '500',
              color: 'var(--blue-accent-light)',
              margin: '0 0 8px 0',
            }}
          >
            Webhook Behavior
          </h3>
          <ul
            style={{
              fontSize: '0.75rem',
              color: 'var(--light-gray)',
              margin: 0,
              paddingLeft: '16px',
              lineHeight: '1.5',
            }}
          >
            <li>Notifications are sent only when a team member's status changes</li>
            <li>Status is checked every minute</li>
            <li>Each notification includes all team members' current status</li>
            <li>Next online times are shown in Discord timestamp format</li>
          </ul>
        </div>
      )}
    </div>
  );
};
